
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useSuppliers } from "@/hooks/useSuppliers";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface SupplierSelectorProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
}

export const SupplierSelector = ({ value, onValueChange, placeholder = "Select supplier..." }: SupplierSelectorProps) => {
  const [open, setOpen] = useState(false);
  const { data: suppliers } = useSuppliers();
  
  const activeSuppliers = suppliers?.filter(s => s.is_active && s.supplier_code) || [];
  const selectedSupplier = activeSuppliers.find(s => s.supplier_code === value);

  return (
    <div className="space-y-2">
      <Label>Supplier *</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedSupplier ? (
              <span className="font-mono text-sm">
                {selectedSupplier.supplier_code}
              </span>
            ) : (
              placeholder
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput placeholder="Search suppliers..." />
            <CommandList>
              <CommandEmpty>No suppliers found.</CommandEmpty>
              <CommandGroup>
                {activeSuppliers.map((supplier) => (
                  <CommandItem
                    key={supplier.id}
                    value={supplier.supplier_code}
                    onSelect={(currentValue) => {
                      onValueChange(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === supplier.supplier_code ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                        {supplier.supplier_code}
                      </span>
                      <span className="text-gray-600 text-sm">({supplier.name})</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
